<template>
  <div class="input-selector-wrapper position-relative" :class="{ disabled: disabled }">
    <input
      :value="displayValue"
      :placeholder="placeholder"
      :disabled="disabled"
      class="form-control bg-light border-0 py-3"
      autocomplete="off"
      @focus="handleFocus"
      @input="handleInput" />
    <i class="fa-solid fa-chevron-down chevron-icon"></i>
    <div v-if="showDropdown && !disabled" class="dropdown-menu show">
      <div
        v-for="option in filteredOptions"
        :key="option.code"
        class="dropdown-item"
        :class="{ active: option.code === modelValue }"
        @click="select(option)">
        {{ option.name }}
      </div>
      <div v-if="!filteredOptions.length" class="dropdown-item text-muted">{{ $t('general.sinResultados') }}</div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, onBeforeMount, onBeforeUnmount } from 'vue';

  const props = defineProps({
    modelValue: { type: String, default: null },
    options: { type: Object, required: true },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  });

  const emit = defineEmits(['update:modelValue']);

  const searchQuery = ref('');
  const showDropdown = ref(false);

  const optionsList = computed(() => Object.entries(props.options).map(([code, name]) => ({ code, name })));

  const filteredOptions = computed(() => {
    if (!searchQuery.value) return optionsList.value;
    const query = searchQuery.value.toLowerCase();
    return optionsList.value.filter(opt => opt.name.toLowerCase().includes(query));
  });

  const displayValue = computed(() => {
    if (showDropdown.value) return searchQuery.value;
    return props.options[props.modelValue] || '';
  });

  function select(option) {
    emit('update:modelValue', option.code);
    searchQuery.value = '';
    showDropdown.value = false;
  }

  function handleFocus() {
    if (!props.disabled) {
      searchQuery.value = '';
      showDropdown.value = true;
    }
  }

  function handleInput(event) {
    if (props.disabled) return;
    searchQuery.value = event.target.value;
    showDropdown.value = true;
    if (!searchQuery.value) emit('update:modelValue', null);
  }

  function handleClickOutside(e) {
    if (!e.target.closest('.input-selector-wrapper')) {
      showDropdown.value = false;
      searchQuery.value = displayValue.value;
    }
  }

  onBeforeMount(() => {
    document.addEventListener('click', handleClickOutside);
    if (props.modelValue) searchQuery.value = props.options[props.modelValue];
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<style lang="scss" scoped>
  .input-selector-wrapper {
    &.disabled {
      opacity: 0.7;
    }

    .form-control {
      //colores hardcodeados para matching con los demas inputs
      background-color: rgba(32, 36, 68, 0.02) !important;
      box-shadow: 0 0 3px 0 rgba(32, 36, 68, 0.35) inset !important;
      border: none !important;
      height: 40px !important;
      padding: 0.375rem 2.5rem 0.375rem 0.75rem !important;
      text-align: left !important;
      transition: background-color 0.2s ease, box-shadow 0.2s ease !important;

      &:hover:not(:disabled) {
        background-color: rgba(var(--bs-primary-rgb), 0.05) !important;
        box-shadow: 0 0 3px 0 rgba(var(--bs-primary-rgb), 0.4) inset !important;
      }

      &:focus {
        outline: none !important;
        box-shadow: 0 0 3px 0 rgba(32, 36, 68, 0.35) inset !important;
      }

      &:disabled {
        background-color: white !important;
      }
    }

    .chevron-icon {
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--bs-primary);
      pointer-events: none;
      font-size: 14px;
      z-index: 10;
    }

    .dropdown-menu {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      max-height: 300px;
      overflow-y: auto;
      background-color: white;
      border: 1px solid rgba(var(--bs-primary-rgb), 0.25) !important;
      border-radius: var(--bs-border-radius) !important;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
      z-index: 1000;
      margin-top: 4px;
      padding: 0;

      .dropdown-item {
        padding: 12px 16px;
        cursor: pointer;
        transition: background-color 0.2s ease !important;
        border-bottom: 1px solid rgba(var(--bs-primary-rgb), 0.1);

        &:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
        }

        &.active {
          background-color: rgba(var(--bs-primary-rgb), 0.15) !important;
          font-weight: 600;
          color: var(--bs-primary) !important;
        }

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    .input-selector-wrapper {
      .form-control,
      .dropdown-item {
        font-size: 13px !important;
      }
    }
  }
</style>
